import type {
  QuestionnaireAnswerMap,
  QuestionnaireQuestionDefinition,
  QuestionnaireSectionDefinition,
} from '@artem-lex/common-server/questionnaire/conditional'
import { isAnswerMissing } from '@artem-lex/common-server/questionnaire/conditional'
import type {
  TypeBQuestionnaireDefinition,
  TypeBQuestionnaireQuestion,
  TypeBQuestionnaireSection,
} from '@/shared/hooks/api/machineryDirectiveCompliance'

export function normalizeTypeBQuestionnaireAnswers(
  definition: TypeBQuestionnaireDefinition,
  raw: Record<string, unknown>
): QuestionnaireAnswerMap {
  const normalized: QuestionnaireAnswerMap = {}
  for (const question of definition.questions) {
    const rawValue = raw[question.questionKey]
    if (rawValue == null) {
      normalized[question.questionKey] = null
      continue
    }
    if (question.kind === 'boolean') {
      normalized[question.questionKey] =
        typeof rawValue === 'boolean' ? rawValue : null
      continue
    }
    if (
      question.kind === 'single_select' &&
      question.questionKey === 'machine_mobility'
    ) {
      normalized[question.questionKey] =
        typeof rawValue === 'string' &&
        definition.enums.machine_mobility.includes(rawValue)
          ? rawValue
          : null
      continue
    }
    if (question.kind === 'text') {
      normalized[question.questionKey] =
        typeof rawValue === 'string' ? rawValue.trim() : null
      continue
    }
    normalized[question.questionKey] = null
  }
  return normalized
}

export function sortTypeBQuestionnaireSections(
  sections: TypeBQuestionnaireSection[]
): TypeBQuestionnaireSection[] {
  return [...sections].sort((first, second) => first.order - second.order)
}

export function getWizardSections(
  definition: TypeBQuestionnaireDefinition
): TypeBQuestionnaireSection[] {
  return sortTypeBQuestionnaireSections(definition.sections)
}

export function getActiveTypeBQuestionnaireSections(params: {
  definition: TypeBQuestionnaireDefinition
  visibleQuestionKeys: Set<string>
}): TypeBQuestionnaireSection[] {
  const { definition, visibleQuestionKeys } = params
  return sortTypeBQuestionnaireSections(definition.sections).filter((section) => {
    return definition.questions.some(
      (question) =>
        question.sectionKey === section.sectionKey &&
        visibleQuestionKeys.has(question.questionKey)
    )
  })
}

export function getSectionQuestions(params: {
  definition: TypeBQuestionnaireDefinition
  sectionKey: string
}): TypeBQuestionnaireQuestion[] {
  const { definition, sectionKey } = params
  return definition.questions
    .filter((question) => question.sectionKey === sectionKey)
    .sort((first, second) => first.order - second.order)
}

export function getSectionVisibleQuestions(params: {
  definition: TypeBQuestionnaireDefinition
  sectionKey: string
  visibleQuestionKeys: Set<string>
}): TypeBQuestionnaireQuestion[] {
  const { definition, sectionKey, visibleQuestionKeys } = params
  return definition.questions
    .filter(
      (question) =>
        question.sectionKey === sectionKey &&
        visibleQuestionKeys.has(question.questionKey)
    )
    .sort((first, second) => first.order - second.order)
}

export function getSectionAnswerValue(params: {
  question: QuestionnaireQuestionDefinition
  answers: QuestionnaireAnswerMap
  textDraftByQuestionKey: Record<string, string>
}): QuestionnaireAnswerMap[string] {
  if (params.question.kind === 'text') {
    const draft = params.textDraftByQuestionKey[params.question.questionKey]
    if (draft != null) {
      return draft
    }
  }
  return params.answers[params.question.questionKey] ?? null
}

export function getSectionIncompleteQuestionKeys(params: {
  sectionQuestions: QuestionnaireQuestionDefinition[]
  visibleQuestionKeys: Set<string>
  answers: QuestionnaireAnswerMap
  textDraftByQuestionKey: Record<string, string>
}): string[] {
  return params.sectionQuestions
    .filter((question) => params.visibleQuestionKeys.has(question.questionKey))
    .filter((question) => question.requiredWhenVisible)
    .filter((question) => {
      const value = getSectionAnswerValue({
        question,
        answers: params.answers,
        textDraftByQuestionKey: params.textDraftByQuestionKey,
      })
      return isAnswerMissing(value)
    })
    .map((question) => question.questionKey)
}

export function isSectionComplete(params: {
  sectionQuestions: QuestionnaireQuestionDefinition[]
  visibleQuestionKeys: Set<string>
  answers: QuestionnaireAnswerMap
  textDraftByQuestionKey: Record<string, string>
}): boolean {
  return getSectionIncompleteQuestionKeys(params).length === 0
}

export function isQuestionnaireComplete(params: {
  wizardSections: QuestionnaireSectionDefinition[]
  definition: TypeBQuestionnaireDefinition
  visibleQuestionKeys: Set<string>
  answers: QuestionnaireAnswerMap
  textDraftByQuestionKey: Record<string, string>
}): boolean {
  for (const section of params.wizardSections) {
    const sectionQuestions = getSectionQuestions({
      definition: params.definition,
      sectionKey: section.sectionKey,
    })
    if (
      !isSectionComplete({
        sectionQuestions,
        visibleQuestionKeys: params.visibleQuestionKeys,
        answers: params.answers,
        textDraftByQuestionKey: params.textDraftByQuestionKey,
      })
    ) {
      return false
    }
  }
  return true
}

export function getSectionMissingQuestionKeys(params: {
  sectionQuestions: QuestionnaireQuestionDefinition[]
  missingRequiredQuestionKeys: Set<string>
}): string[] {
  return params.sectionQuestions
    .map((question) => question.questionKey)
    .filter((questionKey) => params.missingRequiredQuestionKeys.has(questionKey))
}

export type SectionCompletionStats = {
  totalRequiredVisible: number
  answeredRequiredVisible: number
  isComplete: boolean
}

export function getSectionCompletionStats(params: {
  sectionQuestions: QuestionnaireQuestionDefinition[]
  visibleQuestionKeys: Set<string>
  answers: QuestionnaireAnswerMap
  textDraftByQuestionKey: Record<string, string>
}): SectionCompletionStats {
  const requiredVisibleQuestions = params.sectionQuestions.filter(
    (question) =>
      params.visibleQuestionKeys.has(question.questionKey) &&
      question.requiredWhenVisible
  )
  const incompleteKeys = getSectionIncompleteQuestionKeys({
    sectionQuestions: params.sectionQuestions,
    visibleQuestionKeys: params.visibleQuestionKeys,
    answers: params.answers,
    textDraftByQuestionKey: params.textDraftByQuestionKey,
  })
  const totalRequiredVisible = requiredVisibleQuestions.length
  const answeredRequiredVisible = totalRequiredVisible - incompleteKeys.length
  return {
    totalRequiredVisible,
    answeredRequiredVisible,
    isComplete: incompleteKeys.length === 0,
  }
}

export function countIncompleteVisibleQuestions(params: {
  activeSections: QuestionnaireSectionDefinition[]
  definition: TypeBQuestionnaireDefinition
  visibleQuestionKeys: Set<string>
  answers: QuestionnaireAnswerMap
  textDraftByQuestionKey: Record<string, string>
}): number {
  let total = 0
  for (const section of params.activeSections) {
    const sectionQuestions = getSectionQuestions({
      definition: params.definition,
      sectionKey: section.sectionKey,
    })
    total += getSectionIncompleteQuestionKeys({
      sectionQuestions,
      visibleQuestionKeys: params.visibleQuestionKeys,
      answers: params.answers,
      textDraftByQuestionKey: params.textDraftByQuestionKey,
    }).length
  }
  return total
}

export function buildDefaultSectionOpenByKey(params: {
  activeSections: QuestionnaireSectionDefinition[]
  definition: TypeBQuestionnaireDefinition
  visibleQuestionKeys: Set<string>
  answers: QuestionnaireAnswerMap
  textDraftByQuestionKey: Record<string, string>
}): Record<string, boolean> {
  const openByKey: Record<string, boolean> = {}
  for (const section of params.activeSections) {
    const sectionQuestions = getSectionQuestions({
      definition: params.definition,
      sectionKey: section.sectionKey,
    })
    const stats = getSectionCompletionStats({
      sectionQuestions,
      visibleQuestionKeys: params.visibleQuestionKeys,
      answers: params.answers,
      textDraftByQuestionKey: params.textDraftByQuestionKey,
    })
    openByKey[section.sectionKey] = !stats.isComplete
  }
  return openByKey
}

export function findFirstIncompleteSectionIndex(params: {
  wizardSections: QuestionnaireSectionDefinition[]
  definition: TypeBQuestionnaireDefinition
  visibleQuestionKeys: Set<string>
  answers: QuestionnaireAnswerMap
  textDraftByQuestionKey: Record<string, string>
}): number {
  const {
    wizardSections,
    definition,
    visibleQuestionKeys,
    answers,
    textDraftByQuestionKey,
  } = params

  if (wizardSections.length === 0) {
    return 0
  }

  for (let index = 0; index < wizardSections.length; index += 1) {
    const section = wizardSections[index]
    const sectionQuestions = getSectionQuestions({
      definition,
      sectionKey: section.sectionKey,
    })
    if (
      !isSectionComplete({
        sectionQuestions,
        visibleQuestionKeys,
        answers,
        textDraftByQuestionKey,
      })
    ) {
      return index
    }
  }

  return wizardSections.length - 1
}

export function typeBQuestionElementId(questionKey: string): string {
  return `type-b-question-${questionKey}`
}

export function scrollToTypeBQuestion(questionKey: string): void {
  const element = document.getElementById(typeBQuestionElementId(questionKey))
  if (element == null || !(element instanceof HTMLElement)) {
    return
  }
  element.scrollIntoView({ behavior: 'smooth', block: 'center' })
  element.focus({ preventScroll: true })
}

export function findFirstIncompleteQuestionKey(params: {
  activeSections: QuestionnaireSectionDefinition[]
  definition: TypeBQuestionnaireDefinition
  visibleQuestionKeys: Set<string>
  answers: QuestionnaireAnswerMap
  textDraftByQuestionKey: Record<string, string>
}): string | null {
  for (const section of params.activeSections) {
    const sectionQuestions = getSectionQuestions({
      definition: params.definition,
      sectionKey: section.sectionKey,
    })
    const incompleteKeys = getSectionIncompleteQuestionKeys({
      sectionQuestions,
      visibleQuestionKeys: params.visibleQuestionKeys,
      answers: params.answers,
      textDraftByQuestionKey: params.textDraftByQuestionKey,
    })
    if (incompleteKeys.length > 0) {
      return incompleteKeys[0] ?? null
    }
  }
  return null
}

export function expandIncompleteSectionOpenByKey(params: {
  activeSections: QuestionnaireSectionDefinition[]
  definition: TypeBQuestionnaireDefinition
  visibleQuestionKeys: Set<string>
  answers: QuestionnaireAnswerMap
  textDraftByQuestionKey: Record<string, string>
  previousOpenByKey: Record<string, boolean>
}): Record<string, boolean> {
  const next = { ...params.previousOpenByKey }
  for (const section of params.activeSections) {
    const sectionQuestions = getSectionQuestions({
      definition: params.definition,
      sectionKey: section.sectionKey,
    })
    const stats = getSectionCompletionStats({
      sectionQuestions,
      visibleQuestionKeys: params.visibleQuestionKeys,
      answers: params.answers,
      textDraftByQuestionKey: params.textDraftByQuestionKey,
    })
    if (!stats.isComplete) {
      next[section.sectionKey] = true
    }
  }
  return next
}
